import React, { useState, useEffect } from 'react';
import {
  X,
  Calendar,
  Clock,
  Flag,
  Tag,
  Folder,
  Layers,
  Link2,
  Users,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import api from '../../api/client';

export default function TaskCreateModal({
  isOpen,
  onClose,
  onCreated,
  initialProjectId = null,
  initialParentTaskId = null,
  projects = [],
  categories = [],
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [projectId, setProjectId] = useState(initialProjectId || '');
  const [parentTaskId, setParentTaskId] = useState(initialParentTaskId || '');
  const [status, setStatus] = useState('TODO');
  const [priority, setPriority] = useState('MEDIUM');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  );
  const [estimatedMinutes, setEstimatedMinutes] = useState('');
  const [timeLimitMinutes, setTimeLimitMinutes] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState([]);
  const [relatedType, setRelatedType] = useState('');
  const [relatedId, setRelatedId] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Lock body scroll on mobile when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('mobile-modal-open');
    } else {
      document.body.classList.remove('mobile-modal-open');
    }
    return () => document.body.classList.remove('mobile-modal-open');
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setDescription('');
      setProjectId(initialProjectId || '');
      setParentTaskId(initialParentTaskId || '');
      setStatus('TODO');
      setPriority('MEDIUM');
      setStartAt('');
      setEndAt('');
      setEstimatedMinutes('');
      setTimeLimitMinutes('');
      setCategoryId('');
      setTagInput('');
      setTags([]);
      setRelatedType('');
      setRelatedId('');
      setError('');
    }
  }, [isOpen, initialProjectId, initialParentTaskId]);

  if (!isOpen) return null;

  const handleAddTag = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const val = tagInput.trim().replace(/^#/, '').toLowerCase();
      if (val && !tags.includes(val)) {
        setTags([...tags, val]);
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Task title is required.');
      return;
    }

    if (startAt && endAt) {
      if (new Date(endAt) < new Date(startAt)) {
        setError('Due date/time cannot be before start date/time.');
        return;
      }
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || undefined,
        project_id: projectId || undefined,
        parent_task_id: parentTaskId || undefined,
        status,
        priority,
        start_at: startAt ? new Date(startAt).toISOString() : undefined,
        end_at: endAt ? new Date(endAt).toISOString() : undefined,
        timezone,
        estimated_minutes: estimatedMinutes ? parseInt(estimatedMinutes, 10) : undefined,
        time_limit_minutes: timeLimitMinutes ? parseInt(timeLimitMinutes, 10) : undefined,
        category_id: categoryId || undefined,
        tags: tags.length > 0 ? tags : undefined,
        related_resource_type: relatedType || undefined,
        related_resource_id: relatedId || undefined,
      };

      const res = await api.post('/tasks', payload);
      if (onCreated) onCreated(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-drawer-wrapper" role="presentation">
      <div className="card-drawer-overlay animate-fade-in" onClick={onClose} aria-hidden="true" />
      <aside
        className="card-drawer task-drawer task-create-drawer animate-slide-left"
        role="dialog"
        aria-modal="true"
        aria-label={parentTaskId ? 'Add Subtask' : 'Create New Task'}
      >
        {/* Header */}
        <div className="card-drawer-header modal-header task-create-header">
          <div className="task-create-title-group">
            <h2 className="modal-title">
              {parentTaskId ? 'Add Subtask' : 'Create New Task'}
            </h2>
            <p className="modal-subtitle">
              Fill in the details to schedule and assign this task.
            </p>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="task-create-form" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <div className="card-drawer-body task-create-body" style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '14px', flex: 1, overflowY: 'auto' }}>
            {error && (
              <div
                style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  background: 'rgba(239, 68, 68, 0.12)',
                  color: 'var(--danger)',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <AlertCircle size={15} />
                <span>{error}</span>
              </div>
            )}

            {/* Title */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                Task Title <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <input
                type="text"
                className="input-field"
                placeholder="e.g. Implement security headers and CSP policy"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus
                required
                style={{ width: '100%' }}
              />
            </div>

            {/* Project & Category row */}
            <div className="task-form-row-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                  Project
                </label>
                <select
                  className="input-field"
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="">(No Project / Personal Task)</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                  Category
                </label>
                <select
                  className="input-field"
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="">(None)</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Status & Priority */}
            <div className="task-form-row-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                  Initial Status
                </label>
                <select
                  className="input-field"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="BACKLOG">Backlog</option>
                  <option value="TODO">To Do</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="REVIEW">In Review</option>
                  <option value="BLOCKED">Blocked</option>
                  <option value="COMPLETED">Completed</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                  Priority Level
                </label>
                <select
                  className="input-field"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="URGENT">Urgent ⚡</option>
                </select>
              </div>
            </div>

            {/* Dates: Start & End */}
            <div className="task-form-row-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                  Start Date & Time
                </label>
                <input
                  type="datetime-local"
                  className="input-field"
                  value={startAt}
                  onChange={(e) => setStartAt(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                  Due Date & Time
                </label>
                <input
                  type="datetime-local"
                  className="input-field"
                  value={endAt}
                  onChange={(e) => setEndAt(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            {/* Time Tracking: Estimated & Limit */}
            <div className="task-form-row-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                  Estimated (Mins)
                </label>
                <input
                  type="number"
                  min="0"
                  className="input-field"
                  placeholder="e.g. 120"
                  value={estimatedMinutes}
                  onChange={(e) => setEstimatedMinutes(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                  Time Limit (Mins)
                </label>
                <input
                  type="number"
                  min="0"
                  className="input-field"
                  placeholder="e.g. 180"
                  value={timeLimitMinutes}
                  onChange={(e) => setTimeLimitMinutes(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                  Timezone
                </label>
                <input
                  type="text"
                  className="input-field"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                Description & Acceptance Criteria
              </label>
              <textarea
                className="input-field"
                rows={3}
                placeholder="Add background, requirements, checklist notes..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={{ width: '100%', resize: 'vertical' }}
              />
            </div>

            {/* Tags Input */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                Tags
              </label>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px',
                  alignItems: 'center',
                  padding: '6px 10px',
                  background: 'var(--bg-input)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                }}
              >
                {tags.map((tag) => (
                  <span
                    key={tag}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      background: 'rgba(59, 130, 246, 0.18)',
                      color: 'var(--accent-primary)',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                    }}
                  >
                    #{tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'inherit',
                        cursor: 'pointer',
                        padding: 0,
                        display: 'flex',
                      }}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  placeholder={tags.length === 0 ? 'Type tag & press Enter...' : ''}
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleAddTag}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-primary)',
                    outline: 'none',
                    fontSize: '12px',
                    flex: 1,
                    minWidth: '120px',
                  }}
                />
              </div>
            </div>

            {/* Cross-Application Related Resource */}
            <div className="task-form-row-link" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                  Link Resource Type
                </label>
                <select
                  className="input-field"
                  value={relatedType}
                  onChange={(e) => setRelatedType(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="">(None)</option>
                  <option value="DOCUMENT">Document</option>
                  <option value="NOTE">Secure Note</option>
                  <option value="PASSWORD">Password Vault Item</option>
                  <option value="IDENTITY">Identity</option>
                  <option value="CARD">Payment Card</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                  Resource Reference / ID
                </label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="e.g. doc-49a8... (No sensitive secrets)"
                  value={relatedId}
                  onChange={(e) => setRelatedId(e.target.value)}
                  disabled={!relatedType}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </div>

          {/* Footer - Single Row */}
          <div className="card-drawer-footer modal-footer task-create-footer">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={saving || !title.trim()}
            >
              <CheckCircle2 size={15} />
              <span>{saving ? 'Creating...' : parentTaskId ? 'Add Subtask' : 'Create Task'}</span>
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}
